import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import "../css/Login.css";

export default function NewPassword() {
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({ password: "", confirmPassword: "", general: "" });
  const [touched, setTouched] = useState({ password: false, confirmPassword: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // 🟢 Get email & tempPassword passed from Login.js
  const { email, tempPassword } = location.state || {};

  if (!email || !tempPassword) {
    // session expired or direct access
    navigate("/login");
  }

  // 🔑 Password validation rules
  const validatePassword = (value) => {
    if (!value) return "Password is required.";
    if (value.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(value)) return "Must contain at least 1 uppercase letter.";
    if (!/[a-z]/.test(value)) return "Must contain at least 1 lowercase letter.";
    if (!/[0-9]/.test(value)) return "Must contain at least 1 number.";
    if (!/[!@#$%^&*]/.test(value)) return "Must contain at least 1 special character.";
    return "";
  };

  // 🔍 Validate form
  const validate = () => {
    let valid = true;
    const newErrors = { password: "", confirmPassword: "", general: "" };

    const passError = validatePassword(formData.password);
    if (passError) {
      newErrors.password = passError;
      valid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm Password is required.";
      valid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // 📝 Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (submitted) validate();
  };

  // ⌨️ Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.target.name === "password" && !errors.password) {
        confirmPasswordRef.current.focus();
      } else if (e.target.name === "confirmPassword") {
        handleSubmit(e);
      }
    }
  };

  // 🚀 Submit new password
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validate()) return;

    setLoading(true);
    try {
      // 🔹 Re-sign in with temporary password
      const user = await Auth.signIn(email, tempPassword);

      // 🔹 Complete new password challenge
  
    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      await Auth.completeNewPassword(user, formData.password, {
        name: user.name, // Replace with actual required attribute(s)
      });
    }

      navigate("/"); // redirect to dashboard/home
    } catch (error) {
      console.error("Error setting new password:", error);
      setErrors((prev) => ({
        ...prev,
        general: error.message || "Failed to set new password.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="d-flex vh-100">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div
      className="d-flex flex-column justify-content-between align-items-center px-4 py-5 position-relative w-100 h-100"
      style={{ maxWidth: "400px", margin: "0 auto" }}
    >
      <div className="w-100 d-flex flex-column align-items-center">
        <div className="mb-4 mt-5 text-center">
          <img src="/images/Logo.png" alt="Logo" style={{ height: "60px" }} />
        </div>

        <h3 className="fw-bold text-center">Set New Password</h3>
        <p className="text-center text-muted mb-4" style={{ fontSize: "14px" }}>
          Please create a new password to activate your account
        </p>

        <form onSubmit={handleSubmit} className="w-100">
          {/* Password */}
          <div className="mb-3">
            <label
              htmlFor="password"
              className={`form-label fw-semibold mb-2 ${errors.password ? "text-danger" : ""}`}
            >
              New Password
            </label>
            <div className="input-icon-wrapper">
              <input
                type="password"
                name="password"
                ref={passwordRef}
                className={`form-control border-2 custom-focus ${errors.password ? "input-error" : ""}`}
                placeholder="Enter new password"
                value={formData.password}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
              />
              {errors.password && submitted && (
                <img src="/images/failure.svg" alt="Error" className="input-error-icon" />
              )}
            </div>
            {submitted && touched.password && errors.password && (
              <div className="alert-box">
                <img src="/images/security.svg" alt="Info" width={20} height={20} />
                <div className="flex-grow-1">
                  <div className="fw-bold text-dark">Error</div>
                  <div>{errors.password}</div>
                </div>
                <span
                  className="close-icon"
                  onClick={() => setErrors((prev) => ({ ...prev, password: "" }))}
                >
                  ❌
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-3">
            <label
              htmlFor="confirmPassword"
              className={`form-label fw-semibold mb-2 ${errors.confirmPassword ? "text-danger" : ""}`}
            >
              Confirm Password
            </label>
            <div className="input-icon-wrapper">
              <input
                type="password"
                name="confirmPassword"
                ref={confirmPasswordRef}
                className={`form-control border-2 custom-focus ${errors.confirmPassword ? "input-error" : ""}`}
                placeholder="Re-enter new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
              />
              {errors.confirmPassword && submitted && (
                <img src="/images/failure.svg" alt="Error" className="input-error-icon" />
              )}
            </div>
            {submitted && touched.confirmPassword && errors.confirmPassword && (
              <div className="alert-box">
                <img src="/images/security.svg" alt="Info" width={20} height={20} />
                <div className="flex-grow-1">
                  <div className="fw-bold text-dark">Error</div>
                  <div>{errors.confirmPassword}</div>
                </div>
                <span
                  className="close-icon"
                  onClick={() => setErrors((prev) => ({ ...prev, confirmPassword: "" }))}
                >
                  ❌
                </span>
              </div>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="alert-box">
              <img src="/images/security.svg" alt="Info" width={20} height={20} />
              <div className="flex-grow-1">
                <div className="fw-bold text-dark">Error</div>
                <div>{errors.general}</div>
              </div>
              <span
                className="close-icon"
                onClick={() => setErrors((prev) => ({ ...prev, general: "" }))}
              >
                ❌
              </span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-md w-100 mt-3"
            style={{ backgroundColor: "#1A1A52", height: "40px" }}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save New Password"}
          </button>
        </form>
      </div>
    </div>
      </div>
      <div
        className="d-none d-md-flex align-items-center justify-content-center"
        style={{ width: "50%", backgroundColor: "#f8f9fa" }}
      >
        <img
          src="/images/BG.png"
          alt="Side"
          className="img-fluid"
          style={{ maxHeight: "90%", maxWidth: "90%", objectFit: "contain" }}
        />
      </div>
    </div>    

  );
}
